"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

let measureContext: CanvasRenderingContext2D | null | undefined;
function textWidth(text: string, font: string): number | null {
  if (measureContext === undefined) {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  if (!measureContext) return null;
  measureContext.font = font;
  return measureContext.measureText(text).width;
}

/**
 * 후보 문자열 중 **한 줄에 들어가는 가장 긴 것**을 그린다(10-13 카드 지역 줄).
 * 후보는 짧은 것 → 긴 것 순(`cityListCandidates`). 서버 렌더·첫 화면은 `[0]`(가장 짧은 것)
 * — 폭은 브라우저에서만 잴 수 있어서, 그려진 뒤 ResizeObserver 가 폭을 재고 늘린다.
 * 줄 높이는 그대로라 아래 줄이 밀리지 않는다. 어떤 후보도 안 들어가면 `[0]` + 말줄임.
 */
export function FittedText({
  candidates,
  className,
}: {
  candidates: string[];
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [index, setIndex] = useState(0);
  const key = candidates.join("\u0000");

  useEffect(() => {
    const el = ref.current;
    if (!el || candidates.length <= 1) return;
    const measure = () => {
      const available = el.clientWidth;
      if (available <= 0) return;
      const style = getComputedStyle(el);
      const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      let best = 0;
      for (let i = candidates.length - 1; i > 0; i--) {
        const width = textWidth(candidates[i], font);
        if (width !== null && width <= available) {
          best = i;
          break;
        }
      }
      setIndex(best);
    };
    // observe 직후 한 번 불린다 — 첫 측정도 이 콜백이 한다
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // 웹폰트가 늦게 오면 글자 폭이 바뀐다
    document.fonts?.ready.then(measure).catch(() => {});
    return () => observer.disconnect();
    // key 가 후보 목록의 정체 — 배열 객체는 렌더마다 새로 만들어진다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (candidates.length === 0) return null;
  const text = candidates[Math.min(index, candidates.length - 1)];
  return (
    <span ref={ref} className={cn("block truncate", className)} title={candidates[candidates.length - 1]}>
      {text}
    </span>
  );
}
